import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'

export type ValueType = 'fixed_value' | 'llm_value'

export type ArgumentConfig = {
  value: string
  valueType: ValueType
  is_required: boolean
}

export type IntegrationRow = {
  id: number
  campaign_id: number
  hook: string
  integration_type: string
  config: Record<string, ArgumentConfig>
}

export function useCampaignIntegrations(campaignId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['campaign-integrations', campaignId],
    queryFn: async () => {
      return apiClient<IntegrationRow[]>(`/campaigns/${campaignId}/integrations`)
    },
    enabled: !!campaignId && campaignId > 0,
  })
}

export function useUpsertIntegration() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      campaignId: number
      hook: string
      integration_type: string
      config: Record<string, ArgumentConfig>
    }) => {
      return apiClient<IntegrationRow>(`/campaigns/${params.campaignId}/integrations`, {
        method: 'PUT',
        body: JSON.stringify({
          hook: params.hook,
          integration_type: params.integration_type,
          config: params.config,
        }),
      })
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-integrations', variables.campaignId] })
    },
  })
}

export function useDeleteIntegration() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { campaignId: number; hook: string }) => {
      return apiClient<{ message: string }>(
        `/campaigns/${params.campaignId}/integrations/${params.hook}`,
        { method: 'DELETE' }
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-integrations', variables.campaignId] })
    },
  })
}